import { useMemo, useState, useEffect, useRef, ReactNode } from 'react'
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ListFilter,
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  Check,
} from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

// ============================================================================
// Reusable admin data table — global fuzzy search, per-column sorting, faceted
// multi-select filters, numeric/date-range filters, result counts, CSV export
// and pagination. Fully client-side (the admin endpoints return the whole set),
// styled to the vibrant-celebration system, and keyboard/ screen-reader aware.
// ============================================================================

export type ColumnAlign = 'left' | 'right'
export type FilterType = 'facet' | 'number' | 'date'

export type Column<T> = {
  /** Stable unique key for this column. */
  key: string
  /** Column header label. */
  header: string
  /** Primitive value used for sorting, searching and filtering. Return a
   *  number for numeric/date columns (dates as epoch ms), else a string. */
  accessor: (row: T) => string | number | null | undefined
  /** Value written to the CSV export. Defaults to `accessor`; override for
   *  columns whose accessor is a sort key (e.g. epoch-ms dates) so the export
   *  carries the human-readable value instead. */
  csvAccessor?: (row: T) => string | number | null | undefined
  /** Custom cell renderer; defaults to the accessor value as text. */
  cell?: (row: T) => ReactNode
  sortable?: boolean
  /** Include this column's accessor text in the global search. Default true. */
  searchable?: boolean
  align?: ColumnAlign
  /** Adds a column-specific filter control to the toolbar. */
  filter?: FilterType
  /** Map a raw facet value to a friendly label in the filter dropdown. */
  facetLabel?: (value: string) => string
  /** Tailwind class to keep the column from squishing (e.g. 'min-w-[180px]'). */
  widthClass?: string
}

type SortState = { key: string; dir: 'asc' | 'desc' } | null

type DataTableProps<T> = {
  data: T[]
  columns: Column<T>[]
  getRowId: (row: T) => string
  searchPlaceholder?: string
  initialSort?: { key: string; dir: 'asc' | 'desc' }
  emptyMessage?: string
  /** Base filename (without extension) for CSV export. Omit to hide export. */
  csvFilename?: string
  pageSize?: number
}

const norm = (v: unknown): string =>
  v === null || v === undefined ? '' : String(v).toLowerCase().trim()

// Subsequence test: are all chars of `q` found in order within `text`?
const isSubsequence = (q: string, text: string): boolean => {
  let i = 0
  for (let j = 0; j < text.length && i < q.length; j++) {
    if (text[j] === q[i]) i++
  }
  return i === q.length
}

// Returns a match score (lower = better) or -1 for no match. "Smart" ranking:
// whole-query substring beats all-tokens-substring beats subsequence.
const matchScore = (haystack: string, query: string): number => {
  if (!query) return 0
  if (haystack.includes(query)) return 0
  const tokens = query.split(/\s+/).filter(Boolean)
  if (tokens.length > 1 && tokens.every((t) => haystack.includes(t))) return 1
  if (isSubsequence(query.replace(/\s+/g, ''), haystack)) return 2
  return -1
}

const compareValues = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number => {
  const aNil = a === null || a === undefined || a === ''
  const bNil = b === null || b === undefined || b === ''
  if (aNil && bNil) return 0
  if (aNil) return 1 // empties sort last regardless of direction intent
  if (bNil) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true })
}

function DataTable<T>({
  data,
  columns,
  getRowId,
  searchPlaceholder = 'Search…',
  initialSort,
  emptyMessage = 'No results',
  csvFilename,
  pageSize = 25,
}: DataTableProps<T>) {
  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortState>(initialSort ?? null)
  const [facets, setFacets] = useState<Record<string, Set<string>>>({})
  const [ranges, setRanges] = useState<Record<string, { min: string; max: string }>>({})
  const [dates, setDates] = useState<Record<string, { from: string; to: string }>>({})
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(pageSize)

  // Debounce the search input so filtering doesn't run on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQuery(norm(rawQuery)), 180)
    return () => clearTimeout(id)
  }, [rawQuery])

  const colByKey = useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c])),
    [columns],
  )

  // Distinct values (with counts) for each faceted column, for the dropdowns.
  const facetOptions = useMemo(() => {
    const out: Record<string, { value: string; count: number }[]> = {}
    for (const col of columns) {
      if (col.filter !== 'facet') continue
      const counts = new Map<string, number>()
      for (const row of data) {
        const raw = col.accessor(row)
        const v = raw === null || raw === undefined || raw === '' ? '—' : String(raw)
        counts.set(v, (counts.get(v) || 0) + 1)
      }
      out[col.key] = [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value))
    }
    return out
  }, [columns, data])

  const activeFilterCount = useMemo(() => {
    let n = 0
    for (const k of Object.keys(facets)) if (facets[k]?.size) n += facets[k].size
    for (const k of Object.keys(ranges))
      if (ranges[k]?.min || ranges[k]?.max) n += 1
    for (const k of Object.keys(dates)) if (dates[k]?.from || dates[k]?.to) n += 1
    return n
  }, [facets, ranges, dates])

  // search → filter → sort pipeline.
  const processed = useMemo(() => {
    let rows = data

    // Column filters (facet / number-range / date-range).
    rows = rows.filter((row) => {
      for (const col of columns) {
        const raw = col.accessor(row)
        if (col.filter === 'facet') {
          const sel = facets[col.key]
          if (sel && sel.size) {
            const v = raw === null || raw === undefined || raw === '' ? '—' : String(raw)
            if (!sel.has(v)) return false
          }
        } else if (col.filter === 'number') {
          const r = ranges[col.key]
          if (r && (r.min !== '' || r.max !== '')) {
            // Rows with no value are excluded from an active range (not treated as 0).
            if (raw === null || raw === undefined || raw === '') return false
            const n = typeof raw === 'number' ? raw : Number(raw)
            if (!Number.isFinite(n)) return false
            if (r.min !== '' && n < Number(r.min)) return false
            if (r.max !== '' && n > Number(r.max)) return false
          }
        } else if (col.filter === 'date') {
          const d = dates[col.key]
          if (d && (d.from || d.to)) {
            // Rows with no date are excluded from an active range (not epoch 0).
            if (raw === null || raw === undefined || raw === '') return false
            const n = typeof raw === 'number' ? raw : Number(raw)
            if (!Number.isFinite(n)) return false
            if (d.from && n < new Date(d.from + 'T00:00:00').getTime()) return false
            if (d.to && n > new Date(d.to + 'T23:59:59').getTime()) return false
          }
        }
      }
      return true
    })

    // Global fuzzy search across searchable columns.
    if (query) {
      const searchCols = columns.filter((c) => c.searchable !== false)
      rows = rows
        .map((row) => {
          const hay = searchCols
            .map((c) => norm(c.accessor(row)))
            .filter(Boolean)
            .join(' ')
          return { row, score: matchScore(hay, query) }
        })
        .filter((r) => r.score >= 0)
        .sort((a, b) => a.score - b.score)
        .map((r) => r.row)
    }

    // Sort (stable: keep search ranking as tie-break when no explicit sort).
    // Empty/null values always sort last, independent of direction.
    if (sort) {
      const col = colByKey[sort.key]
      if (col) {
        const dir = sort.dir === 'asc' ? 1 : -1
        const isNil = (v: unknown) => v === null || v === undefined || v === ''
        rows = [...rows].sort((a, b) => {
          const av = col.accessor(a)
          const bv = col.accessor(b)
          const aNil = isNil(av)
          const bNil = isNil(bv)
          if (aNil && bNil) return 0
          if (aNil) return 1
          if (bNil) return -1
          return compareValues(av, bv) * dir
        })
      }
    }

    return rows
  }, [data, columns, colByKey, facets, ranges, dates, query, sort])

  // Reset to first page whenever the result set changes shape.
  useEffect(() => {
    setPage(0)
  }, [query, facets, ranges, dates, size])

  const total = processed.length
  const pageCount = Math.max(1, Math.ceil(total / size))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = useMemo(
    () => processed.slice(clampedPage * size, clampedPage * size + size),
    [processed, clampedPage, size],
  )

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      if (prev.dir === 'asc') return { key, dir: 'desc' }
      return null
    })
  }

  const clearAll = () => {
    setRawQuery('')
    setQuery('')
    setFacets({})
    setRanges({})
    setDates({})
  }

  const exportCsv = () => {
    const esc = (v: unknown) => {
      const s = v === null || v === undefined ? '' : String(v)
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const header = columns.map((c) => esc(c.header)).join(',')
    const lines = processed.map((row) =>
      columns.map((c) => esc((c.csvAccessor || c.accessor)(row))).join(','),
    )
    const csv = [header, ...lines].join('\r\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${csvFilename || 'export'}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const hasActiveState = !!query || activeFilterCount > 0

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4a3d66]/60 dark:text-gray-500" />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-10 rounded-full border-[#1b1033]/12 bg-white pl-9 pr-9 dark:border-white/10 dark:bg-[#1e1438]"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery('')}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4a3d66]/60 transition-colors hover:text-[#ff4d8d] dark:text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Column filters */}
        {columns
          .filter((c) => c.filter)
          .map((col) => (
            <ColumnFilter
              key={col.key}
              column={col}
              facetOptions={facetOptions[col.key] || []}
              facetSelected={facets[col.key]}
              onFacetChange={(next) =>
                setFacets((prev) => ({ ...prev, [col.key]: next }))
              }
              range={ranges[col.key]}
              onRangeChange={(next) =>
                setRanges((prev) => ({ ...prev, [col.key]: next }))
              }
              date={dates[col.key]}
              onDateChange={(next) =>
                setDates((prev) => ({ ...prev, [col.key]: next }))
              }
            />
          ))}

        {hasActiveState && (
          <Button
            variant="ghost"
            onClick={clearAll}
            className="h-10 rounded-full px-3 text-sm font-medium text-[#4a3d66] hover:text-[#ff4d8d] dark:text-gray-400"
          >
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span
            role="status"
            aria-live="polite"
            className="whitespace-nowrap text-sm text-[#4a3d66] dark:text-gray-400 tabular-nums"
          >
            {total.toLocaleString()} {total === 1 ? 'result' : 'results'}
          </span>
          {csvFilename && (
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={total === 0}
              className="h-10 rounded-full border-[#1b1033]/12 bg-white px-4 text-sm font-semibold text-[#1b1033] hover:border-[#ff4d8d]/40 hover:text-[#ff4d8d] dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
            >
              <Download className="mr-1.5 h-4 w-4" />
              CSV
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="glass-card rounded-bubble overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1b1033]/[0.03] dark:bg-white/[0.04] border-b border-[#1b1033]/10 dark:border-white/10">
              <tr>
                {columns.map((col) => {
                  const isSorted = sort?.key === col.key
                  const sortable = col.sortable !== false
                  return (
                    <th
                      key={col.key}
                      aria-sort={
                        isSorted
                          ? sort!.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      className={`px-6 py-3.5 text-xs font-semibold uppercase tracking-wide text-[#4a3d66] dark:text-gray-400 ${
                        col.align === 'right' ? 'text-right' : 'text-left'
                      } ${col.widthClass || ''}`}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className={`group inline-flex items-center gap-1.5 rounded-md transition-colors hover:text-[#ff4d8d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/40 ${
                            col.align === 'right' ? 'flex-row-reverse' : ''
                          } ${isSorted ? 'text-[#ff4d8d]' : ''}`}
                        >
                          <span>{col.header}</span>
                          {isSorted ? (
                            sort!.dir === 'asc' ? (
                              <ChevronUp className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
                          )}
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1b1033]/8 dark:divide-white/10">
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-14 text-center"
                  >
                    <p className="text-sm text-[#4a3d66] dark:text-gray-400">
                      {hasActiveState
                        ? 'No results match your search or filters.'
                        : emptyMessage}
                    </p>
                    {hasActiveState && (
                      <button
                        type="button"
                        onClick={clearAll}
                        className="mt-2 text-sm font-semibold text-[#ff4d8d] hover:underline"
                      >
                        Clear search & filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => (
                  <tr
                    key={getRowId(row)}
                    className="hover:bg-[#1b1033]/[0.02] dark:hover:bg-white/5"
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-6 py-4 ${
                          col.align === 'right'
                            ? 'text-right tabular-nums'
                            : 'text-left'
                        }`}
                      >
                        {col.cell ? col.cell(row) : renderDefault(col.accessor(row))}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#1b1033]/10 px-6 py-3.5 dark:border-white/10">
            <div className="flex items-center gap-2 text-sm text-[#4a3d66] dark:text-gray-400">
              <span className="tabular-nums">
                {clampedPage * size + 1}–
                {Math.min((clampedPage + 1) * size, total)} of{' '}
                {total.toLocaleString()}
              </span>
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                aria-label="Rows per page"
                className="rounded-lg border border-[#1b1033]/12 bg-white px-2 py-1 text-sm text-[#1b1033] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/40 dark:border-white/10 dark:bg-[#1e1438] dark:text-[#f4f0ff]"
              >
                {[25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
                aria-label="Previous page"
                className="h-9 w-9 rounded-full border-[#1b1033]/12 bg-white dark:border-white/10 dark:bg-[#1e1438]"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-1 text-sm text-[#4a3d66] dark:text-gray-400 tabular-nums">
                {clampedPage + 1} / {pageCount}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={clampedPage >= pageCount - 1}
                aria-label="Next page"
                className="h-9 w-9 rounded-full border-[#1b1033]/12 bg-white dark:border-white/10 dark:bg-[#1e1438]"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const renderDefault = (v: string | number | null | undefined): ReactNode => {
  if (v === null || v === undefined || v === '') return <span className="text-[#4a3d66]/50 dark:text-gray-600">—</span>
  return <span className="text-[#4a3d66] dark:text-gray-400">{String(v)}</span>
}

// --- Column filter popover (facet / number-range / date-range) --------------

type ColumnFilterProps<T> = {
  column: Column<T>
  facetOptions: { value: string; count: number }[]
  facetSelected?: Set<string>
  onFacetChange: (next: Set<string>) => void
  range?: { min: string; max: string }
  onRangeChange: (next: { min: string; max: string }) => void
  date?: { from: string; to: string }
  onDateChange: (next: { from: string; to: string }) => void
}

function ColumnFilter<T>({
  column,
  facetOptions,
  facetSelected,
  onFacetChange,
  range,
  onRangeChange,
  date,
  onDateChange,
}: ColumnFilterProps<T>) {
  const activeCount =
    column.filter === 'facet'
      ? facetSelected?.size || 0
      : column.filter === 'number'
        ? range && (range.min !== '' || range.max !== '')
          ? 1
          : 0
        : date && (date.from || date.to)
          ? 1
          : 0

  const label = (v: string) =>
    v === '—' ? '—' : column.facetLabel ? column.facetLabel(v) : v

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-10 rounded-full border-dashed border-[#1b1033]/20 bg-white px-3.5 text-sm font-medium dark:border-white/15 dark:bg-[#1e1438] ${
            activeCount
              ? 'border-solid border-[#ff4d8d]/50 text-[#ff4d8d]'
              : 'text-[#4a3d66] hover:text-[#1b1033] dark:text-gray-400 dark:hover:text-[#f4f0ff]'
          }`}
        >
          <ListFilter className="mr-1.5 h-4 w-4" />
          {column.header}
          {activeCount > 0 && (
            <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#ff4d8d] px-1.5 text-[11px] font-bold text-white tabular-nums">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-60 rounded-2xl border-[#1b1033]/10 bg-white p-2 shadow-celebrate-md dark:border-white/10 dark:bg-[#1e1438]"
      >
        {column.filter === 'facet' && (
          <div className="max-h-64 overflow-y-auto">
            {facetOptions.length === 0 ? (
              <p className="px-2 py-3 text-sm text-[#4a3d66] dark:text-gray-400">
                No values
              </p>
            ) : (
              facetOptions.map((opt) => {
                const checked = facetSelected?.has(opt.value) || false
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() => {
                      const next = new Set(facetSelected || [])
                      if (checked) next.delete(opt.value)
                      else next.add(opt.value)
                      onFacetChange(next)
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm text-[#1b1033] transition-colors hover:bg-[#ff4d8d]/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/40 dark:text-[#f4f0ff] dark:hover:bg-white/5"
                  >
                    <span
                      className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[5px] border transition-colors ${
                        checked
                          ? 'border-[#ff4d8d] bg-[#ff4d8d] text-white'
                          : 'border-[#1b1033]/25 dark:border-white/25'
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate capitalize">{label(opt.value)}</span>
                    <span className="text-xs text-[#4a3d66]/60 tabular-nums dark:text-gray-500">
                      {opt.count}
                    </span>
                  </button>
                )
              })
            )}
            {(facetSelected?.size || 0) > 0 && (
              <button
                type="button"
                onClick={() => onFacetChange(new Set())}
                className="mt-1 w-full rounded-lg px-2 py-1.5 text-center text-xs font-semibold text-[#ff4d8d] hover:bg-[#ff4d8d]/8"
              >
                Clear {column.header}
              </button>
            )}
          </div>
        )}

        {column.filter === 'number' && (
          <div className="space-y-2 p-1">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                inputMode="decimal"
                value={range?.min ?? ''}
                onChange={(e) =>
                  onRangeChange({ min: e.target.value, max: range?.max ?? '' })
                }
                placeholder="Min"
                aria-label={`${column.header} minimum`}
                className="h-9 rounded-lg border-[#1b1033]/12 bg-white text-sm dark:border-white/10 dark:bg-[#130b26]"
              />
              <span className="text-[#4a3d66] dark:text-gray-500">–</span>
              <Input
                type="number"
                inputMode="decimal"
                value={range?.max ?? ''}
                onChange={(e) =>
                  onRangeChange({ min: range?.min ?? '', max: e.target.value })
                }
                placeholder="Max"
                aria-label={`${column.header} maximum`}
                className="h-9 rounded-lg border-[#1b1033]/12 bg-white text-sm dark:border-white/10 dark:bg-[#130b26]"
              />
            </div>
            {(range?.min || range?.max) && (
              <button
                type="button"
                onClick={() => onRangeChange({ min: '', max: '' })}
                className="w-full rounded-lg px-2 py-1.5 text-center text-xs font-semibold text-[#ff4d8d] hover:bg-[#ff4d8d]/8"
              >
                Clear {column.header}
              </button>
            )}
          </div>
        )}

        {column.filter === 'date' && (
          <div className="space-y-2 p-1">
            <label className="block text-xs font-medium text-[#4a3d66] dark:text-gray-400">
              From
              <Input
                type="date"
                value={date?.from ?? ''}
                onChange={(e) =>
                  onDateChange({ from: e.target.value, to: date?.to ?? '' })
                }
                className="mt-1 h-9 rounded-lg border-[#1b1033]/12 bg-white text-sm dark:border-white/10 dark:bg-[#130b26]"
              />
            </label>
            <label className="block text-xs font-medium text-[#4a3d66] dark:text-gray-400">
              To
              <Input
                type="date"
                value={date?.to ?? ''}
                onChange={(e) =>
                  onDateChange({ from: date?.from ?? '', to: e.target.value })
                }
                className="mt-1 h-9 rounded-lg border-[#1b1033]/12 bg-white text-sm dark:border-white/10 dark:bg-[#130b26]"
              />
            </label>
            {(date?.from || date?.to) && (
              <button
                type="button"
                onClick={() => onDateChange({ from: '', to: '' })}
                className="w-full rounded-lg px-2 py-1.5 text-center text-xs font-semibold text-[#ff4d8d] hover:bg-[#ff4d8d]/8"
              >
                Clear {column.header}
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default DataTable
